import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Wifi, X, Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

import { BACKEND_URL } from '../config'  // Auto-fixed: use centralized config;
const API_BASE = `${BACKEND_URL}/api`;

const WiFiAccessModal = ({ isOpen, onClose, propertyId, propertyTitle }) => {
  const [email, setEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [loading, setLoading] = useState(false);
  const [wifiCredentials, setWifiCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address to access WiFi credentials.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/wifi-access`, {
        property_id: propertyId,
        email: email,
        guest_name: guestName || undefined,
      });

      setWifiCredentials(response.data.wifi_credentials);
      toast({
        title: 'WiFi credentials received!',
        description: 'You can now connect to the property WiFi.',
      });
    } catch (error) {
      console.error('Error getting WiFi credentials:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to get WiFi credentials. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast({
      title: 'Copied!',
      description: `${field} copied to clipboard`,
    });
  };

  const handleClose = () => {
    setEmail('');
    setGuestName('');
    setWifiCredentials(null);
    onClose();
  };

  const generateWiFiQRCode = () => {
    if (!wifiCredentials) return '';
    return `WIFI:T:WPA;S:${wifiCredentials.ssid};P:${wifiCredentials.password};;`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wifi className="w-5 h-5" />
            WiFi Access - {propertyTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {!wifiCredentials ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-6">
                <p className="text-gray-600 text-sm">
                  Enter your email address to receive WiFi credentials for this property.
                </p>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label htmlFor="guestName">Guest Name (Optional)</Label>
                <Input
                  id="guestName"
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-rose-500 hover:bg-rose-600"
                disabled={loading}
              >
                {loading ? 'Getting credentials...' : 'Get WiFi Credentials'}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-center py-4 bg-white rounded-lg border-2 border-gray-200">
                <QRCodeSVG
                  value={generateWiFiQRCode()}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>

              <div className="text-center text-sm text-gray-600">
                <p className="font-medium mb-1">Scan to connect automatically</p>
                <p className="text-xs">or use the credentials below</p>
              </div>

              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-600">Network Name (SSID)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={wifiCredentials.ssid}
                      readOnly
                      className="bg-white"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(wifiCredentials.ssid, 'Network name')}
                    >
                      {copiedField === 'Network name' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-600">Password</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={wifiCredentials.password}
                      readOnly
                      className="bg-white font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(wifiCredentials.password, 'Password')}
                    >
                      {copiedField === 'Password' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleClose}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WiFiAccessModal;
